import { useEffect, useRef, useState } from 'react';
import type { CellValue, Property, PropertyOption } from '@noti/shared';

import { fetchDataSource, putDataSourceProperties, updateDataSourceRow } from '../api';
import type { DataSourceDetail } from '../api';
import { t } from '../i18n';
import { DatabaseCell } from './DatabaseCell';

export function DatabaseRowProperties({
  dataSourceId,
  rowId,
  locked,
  people,
  onOpenRow,
}: {
  dataSourceId: string;
  rowId: string;
  locked: boolean;
  people: { id: string; name: string }[];
  onOpenRow: (id: string) => void;
}): React.ReactElement {
  const [source, setSource] = useState<DataSourceDetail | null>(null);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const revision = useRef(0);
  const writes = useRef(Promise.resolve());
  useEffect(() => {
    let active = true;
    setError('');
    void fetchDataSource(dataSourceId)
      .then((next) => {
        if (active) setSource(next);
      })
      .catch(() => {
        if (active) setError(t('database.loadFailed'));
      });
    return () => {
      active = false;
    };
  }, [dataSourceId, reload]);

  const persist = (operation: () => Promise<unknown>, refresh: boolean): void => {
    const version = ++revision.current;
    writes.current = writes.current.then(async () => {
      try {
        await operation();
        if (refresh && version === revision.current) {
          const next = await fetchDataSource(dataSourceId);
          if (version === revision.current) setSource(next);
        }
      } catch {
        setError(t('database.savePropertiesFailed'));
      }
    });
  };
  const write = (id: string, property: Property, value: CellValue): void => {
    if (locked) return;
    setSource((current) =>
      current
        ? {
            ...current,
            rows: current.rows.map((row) =>
              row.id === id ? { ...row, values: { ...row.values, [property.id]: value } } : row,
            ),
          }
        : current,
    );
    persist(
      () => updateDataSourceRow(dataSourceId, id, { propId: property.id, value }),
      property.type === 'relation' ||
        Boolean(
          source?.properties.some((item) => item.type === 'rollup' || item.type === 'edited'),
        ),
    );
  };
  const changeOptions = (
    property: Property,
    mutate: (options: PropertyOption[]) => PropertyOption[],
  ): void => {
    if (locked || !source) return;
    const properties = source.properties.map((item) =>
      item.id === property.id ? { ...item, options: mutate(item.options) } : item,
    );
    setSource({ ...source, properties });
    persist(() => putDataSourceProperties(dataSourceId, properties), true);
  };
  const storedRow = source?.rows.find((row) => row.id === rowId);
  const row = storedRow ? { ...storedRow, color: null } : null;
  return (
    <>
      <section className="row-properties" aria-label={t('database.properties')}>
        {error ? (
          <div role="alert">
            <p>{error}</p>
            <button type="button" className="row" onClick={() => setReload((value) => value + 1)}>
              {t('database.reloadProperties')}
            </button>
          </div>
        ) : null}
        {!source && !error ? <p role="status">{t('database.loading')}</p> : null}
        {source && !row && !error ? <p role="alert">{t('database.rowUnavailable')}</p> : null}
        {row && source ? (
          <fieldset disabled={locked}>
            <legend className="sr-only">{t('database.properties')}</legend>
            <dl>
              {source.properties
                .filter((property) => property.type !== 'title')
                .map((property) => (
                  <div className="row-properties__item" key={property.id}>
                    <dt>{property.name}</dt>
                    <dd>
                      <DatabaseCell
                        row={row}
                        property={property}
                        people={people}
                        relationOptions={source.relationOptions}
                        onOpenRow={onOpenRow}
                        onWrite={write}
                        onCreateOption={(target, name) =>
                          changeOptions(target, (options) => [...options, { n: name, c: 'grey' }])
                        }
                        onRenameOption={(target, value, name) =>
                          changeOptions(target, (options) =>
                            options.map((option) =>
                              option.n === value ? { ...option, n: name } : option,
                            ),
                          )
                        }
                        onRecolorOption={(target, value, color) =>
                          changeOptions(target, (options) =>
                            options.map((option) =>
                              option.n === value ? { ...option, c: color } : option,
                            ),
                          )
                        }
                        onRemoveOption={(target, value) =>
                          changeOptions(target, (options) =>
                            options.filter((option) => option.n !== value),
                          )
                        }
                      />
                    </dd>
                  </div>
                ))}
            </dl>
          </fieldset>
        ) : null}
      </section>
      <hr className="row-properties__divider" />
    </>
  );
}
